import { useEffect, useRef, useState } from 'react';
import { InferType } from 'yup';
import { commentsSchema } from '../schema/comments.schema.ts';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import {
  useCreateTaskCommentMutation,
  useOwnProfileQuery,
  useTaskQuery,
} from '@common/cache';

type IFormShape = InferType<typeof commentsSchema>;

const initialValues = {
  comment: '',
};

export const useCommentsTab = ({
  task_id,
  setIsFormClean,
  tabIndex,
  setNewTabIndex,
  triggerTabChange,
  setTriggerTabChange,
  isFormClean,
  triggerCloseDrawer,
  setTriggerCloseDrawer,
  closeDrawer,
}: {
  task_id: string;
  isFormClean: boolean;
  setIsFormClean: (clean: boolean) => void;
  tabIndex: string;
  setNewTabIndex: (index: string) => void;
  triggerTabChange: boolean;
  setTriggerTabChange: (trigger: boolean) => void;
  triggerCloseDrawer: boolean;
  setTriggerCloseDrawer: (trigger: boolean) => void;
  closeDrawer: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [triggerOpenCreateComment, setTriggerOpenCreateComment] =
    useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [commentToEditId, setCommentToEditId] = useState('');
  const [newCommentToEditId, setNewCommentToEditId] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  const { data: task } = useTaskQuery({ task_id });
  const { data: profile = { id: '' } } = useOwnProfileQuery();
  const { mutateAsync: createCommentAsync, isPending: isSubmitting } =
    useCreateTaskCommentMutation(task_id);

  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(commentsSchema),
    defaultValues: {
      ...initialValues,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = formMethods;

  const handleSetCommentToEdit = (id: string) => {
    setCommentToEditId(id);
  };

  const handleRemoveCommentToEdit = () => {
    setCommentToEditId('');
    setIsFormClean(true);
  };

  const handleOpenCreateComment = () => {
    if (commentToEditId && !isFormClean) {
      setTriggerOpenCreateComment(true);
    } else {
      setIsOpen(true);
      setCommentToEditId('');
      setNewCommentToEditId('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const forceOpenCreateComment = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    setIsOpen(true);
    setCommentToEditId('');
    setNewCommentToEditId('');
    setTriggerOpenCreateComment(false);
  };

  const handleCloseCreateComment = () => {
    setIsOpen(false);
    reset();
  };

  const submitHandler = async (values: IFormShape) => {
    try {
      await createCommentAsync({ comment: values.comment || '' });

      reset();
      setIsFormClean(true);
      setIsOpen(false);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    setNewTabIndex(tabIndex);
    if (triggerTabChange) {
      setTriggerTabChange(false);
    }
    if (triggerCloseDrawer) {
      setTriggerCloseDrawer(false);
    }
    if (newCommentToEditId) {
      setCommentToEditId('');
      setNewCommentToEditId('');
    }
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    reset();
    setIsOpen(false);
    setIsFormClean(true);
    if (newCommentToEditId) {
      setCommentToEditId(newCommentToEditId);
      setNewCommentToEditId('');
    }
    if (triggerCloseDrawer) {
      closeDrawer();
    }
  };

  const handleSaveChanges = async () => {
    await onSubmit();
    reset();
    setIsFormClean(true);
    setIsSaveChangesModalOpen(false);
    if (newCommentToEditId) {
      setCommentToEditId(newCommentToEditId);
      setNewCommentToEditId('');
    }
    if (triggerCloseDrawer) {
      closeDrawer();
    }
  };

  useEffect(() => {
    setIsFormClean(!isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
    }
  }, [triggerTabChange]);

  useEffect(() => {
    if (triggerCloseDrawer && isDirty) {
      setIsSaveChangesModalOpen(true);
    }
  }, [triggerCloseDrawer]);

  useEffect(() => {
    if (isDirty && newCommentToEditId) {
      setIsSaveChangesModalOpen(true);
    }
  }, [newCommentToEditId]);

  return {
    formMethods,
    onSubmit,
    isOpen,
    handleOpenCreateComment,
    handleCloseCreateComment,
    isValid,
    isSubmitting,
    user_id: profile.id,
    commentToEditId,
    handleSetCommentToEdit,
    handleRemoveCommentToEdit,
    newCommentToEditId,
    setNewCommentToEditId,
    setCommentToEditId,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    triggerOpenCreateComment,
    setTriggerOpenCreateComment,
    inputRef,
    forceOpenCreateComment,
    canAddComment: task?.can_add_comment,
  };
};
